import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ArtifactService } from '../../../../core/artifact/artifact.service';
import { quickJsActions } from '../../../../quickjs/quickjs.actions';
import { selectNavigatorCurrentStepId } from '../../../../quickjs/quickjs.feature';

/**
 * Storing what the crew captures on a step — photos, and the customer's signature.
 *
 * Both are canvas data URLs that used to live in a component field and die with it (#43). Both now
 * take the same route: upload the image as an artifact the moment it is captured, keep only the id,
 * and let `postNavigatorStepComment` attach it to that step's system comment on advance.
 *
 * **Images never enter `navigatorData`.** A phone photo is 1-3MB as base64 and that field is a JSON
 * string rewritten on every step advance and every inventory patch — the same field #48's clobbers
 * already contend over. Only ids and small metadata go in.
 *
 * Shared rather than copied because the two components had already drifted once: photos and
 * signatures were written months apart and only photos ever got storage. Anything reading these
 * entries back (`readStepAttachments` in `job-workorders.effects.ts`) depends on the shape below.
 */

/** What a captured entry looks like inside `navigatorData`, keyed by the component's own key. */
export interface StepCaptureEntry {
    /** Which navigator step this belongs to. Read from the slice, never inferred from the key. */
    stepId: string;
    artifactIds: string[];
    /** Distinguishes a signature from a photo when the comment is assembled. */
    kind?: 'photo' | 'signature';
    annotations?: Array<{ reason: string; custom: string }>;
}

/**
 * Turn a canvas data URL into an uploadable file.
 *
 * Kept out of the components because getting it subtly wrong is silent: a bad MIME type produces an
 * artifact the viewer cannot render, and the failure only shows up much later in the activity feed.
 */
export function dataUrlToFile(dataUrl: string, fileName: string): File {
    const [ header, base64 ] = dataUrl.split(',');
    const mime = /:(.*?);/.exec(header)?.[1] ?? 'image/jpeg';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) { bytes[i] = binary.charCodeAt(i); }
    return new File([ bytes ], fileName, { type: mime });
}

/**
 * Upload one captured image and return its artifact id, or `null` if it could not be stored.
 *
 * **Never throws and never blocks.** A crew in a driveway with no signal must still be able to
 * finish the move, so callers surface a warning and a retry rather than gating the step — the
 * mistake #54 had just made in the opposite direction.
 */
export function uploadCapture(
    artifactService: ArtifactService,
    dataUrl: string,
    fileName: string,
): Observable<string | null> {
    let file: File;
    try {
        file = dataUrlToFile(dataUrl, fileName);
    } catch (err) {
        console.error('[navigator-capture] could not build a file from the canvas', err);
        return of(null);
    }

    // Same path manual job comments use, so the result lands on the activity feed identically.
    return artifactService.uploadCommentArtifacts([ file ]).pipe(
        map((artifacts) => artifacts?.[0]?.id ?? null),
        catchError((err) => {
            console.error('[navigator-capture] upload failed', err?.message ?? err, err);
            return of(null);
        }),
    );
}

/**
 * Record a step's captured artifact ids in `navigatorData`, under the component's own key.
 *
 * The event comes from the **route**, not from a prop: `<PhotoCapture>` and `<CustomerSignature>`
 * are declared in the navigator script without an `eventId`, so reading the prop alone would make
 * this silently do nothing — captured, uploaded, never recorded. A script-set prop still wins.
 *
 * The step id comes from the quickjs slice, which is where the crew's actual position lives since
 * #46. It is stored **explicitly** because the component key bears no relation to the step id — the
 * script uses `stepKey="load_truck_photo"` on step `load_complete`, so inferring one from the other
 * fails on exactly the steps that have captures.
 */
export function persistStepCapture(
    deps: { store: Store; route: ActivatedRoute },
    captureKey: string,
    entry: Omit<StepCaptureEntry, 'stepId'>,
): void {
    if (!captureKey) { return; }

    const eventId = deps.route.snapshot.paramMap.get('eventId') ?? '';
    if (!eventId) { return; }

    const stepId = deps.store.selectSignal(selectNavigatorCurrentStepId)() ?? '';

    deps.store.dispatch(quickJsActions.navigatorDataPatched({
        eventId,
        patch: { [captureKey]: { stepId, ...entry } },
    }));
}
